const Bugout = require('bugout')
const Network = require('../../network').default
const session = require('../../network/session').setup()
const networkConfig = require('./network-config')

import { get } from 'lodash-es'
import { buildRoundRobinFixtures } from '../../tournament/round-robin'
import { buildKnockoutBracket } from '../../tournament/knockout'
import { createStandings, applyMatchResult } from '../../tournament/standings'
import { getNextPendingFixture, applyKnockoutAdvance, settleByes } from '../../tournament/progression'

function createDefaultRoom() {
  return {
    gameHost: null,
    screen: null,
    players: [],
    selectedPlayers: [],
    playing: false,
    lobby: {
      slots: {
        1: null,
        2: null,
      },
      locked: false,
    },
    tournament: {
      mode: 'casual',
      status: 'idle',
      participants: [],
      fixtures: [],
      currentFixtureId: null,
      results: [],
      standings: {},
      bracket: null,
      config: {
        targetScore: 5,
        roundRobinLegs: 1,
        knockoutSeeding: 'join_order',
      },
      championIdentity: null,
    },
  }
}

function isValidSlot(slotId) {
  return slotId === 1 || slotId === 2 || slotId === '1' || slotId === '2'
}

function normalizeSlotId(slotId) {
  return Number(slotId)
}

function buildCasualSelectedPlayersFromSlots(state) {
  state.room.selectedPlayers = []
  if (state.room.lobby.slots[1]) {
    state.room.selectedPlayers.push({ id: 1, identity: state.room.lobby.slots[1] })
  }
  if (state.room.lobby.slots[2]) {
    state.room.selectedPlayers.push({ id: 2, identity: state.room.lobby.slots[2] })
  }
}

function ensureRoomShape(state) {
  if (!state.room.lobby) {
    state.room.lobby = { slots: { 1: null, 2: null }, locked: false }
  }
  if (!state.room.lobby.slots) {
    state.room.lobby.slots = { 1: null, 2: null }
  }
  if (!state.room.tournament) {
    state.room.tournament = createDefaultRoom().tournament
  }
  if (!state.room.selectedPlayers) {
    state.room.selectedPlayers = []
  }
}

function upsertFixtureResult(fixtures, result) {
  const fixture = fixtures.find((f) => f.id === result.fixtureId)
  if (!fixture) {
    return null
  }
  fixture.status = 'done'
  fixture.score1 = result.score1
  fixture.score2 = result.score2
  fixture.winnerIdentity = result.winnerIdentity
  return fixture
}

const DEBUG_BOT_IDENTITY_PREFIX = 'debug-bot-'
const DEBUG_BOT_PROFILES = ['easy', 'normal', 'hard']

function isDebugBotIdentity(identity) {
  return typeof identity === 'string' && identity.startsWith(DEBUG_BOT_IDENTITY_PREFIX)
}

function parseDebugBotNumber(identity) {
  if (!isDebugBotIdentity(identity)) {
    return null
  }
  const value = Number(identity.slice(DEBUG_BOT_IDENTITY_PREFIX.length))
  return Number.isFinite(value) && value > 0 ? value : null
}

function sortDebugBots(botA, botB) {
  return (parseDebugBotNumber(botA.identity) || 0) - (parseDebugBotNumber(botB.identity) || 0)
}

function getDebugBots(state) {
  return state.room.players.filter((player) => isDebugBotIdentity(player.identity))
}

function buildDebugBotIdentity(number) {
  return `${DEBUG_BOT_IDENTITY_PREFIX}${number}`
}

function createDebugBot(number) {
  return {
    identity: buildDebugBotIdentity(number),
    peerId: null,
    chosenName: `Debug Bot ${number}`,
    connected: true,
    isBot: true,
    botProfile: 'normal',
  }
}

function isHostRequester(state, message) {
  const requesterIdentity = get(message, 'cid')
  if (!requesterIdentity) {
    return false
  }
  if (!state.room.gameHost) {
    return true
  }
  return state.room.gameHost === requesterIdentity
}

const METRICS_RATE_WINDOW_MS = 10000
const METRICS_PING_INTERVAL_MS = 2000
const METRICS_MAX_RTT_SAMPLES = 40

function nowMs() {
  return Date.now()
}

function estimateBytes(payload) {
  try {
    return JSON.stringify(payload).length
  } catch (e) {
    return 0
  }
}

function createDefaultMetrics() {
  return {
    role: null,
    connectAttemptStartedAt: null,
    connectedAt: null,
    connectDurationMs: null,
    reconnectCount: 0,
    lastDisconnectAt: null,
    bugs: {
      seenAt: null,
      leftAt: null,
      timeoutAt: null,
      serverAt: null,
      announceAt: null,
      trackerAt: null,
      wireSeenAt: null,
      wireLeftAt: null,
      wires: 0,
      trackerPeers: null,
    },
    messages: {
      inCount: 0,
      outCount: 0,
      inBytes: 0,
      outBytes: 0,
      inRateBytesPerSec: 0,
      outRateBytesPerSec: 0,
      inWindow: [],
      outWindow: [],
    },
    latency: {
      lastRttMs: null,
      avgRttMs: null,
      p95RttMs: null,
      samples: [],
      pendingPings: {},
      lastPingSentAt: null,
      lastPongAt: null,
    },
    pingIntervalMs: METRICS_PING_INTERVAL_MS,
    pingTimerId: null,
  }
}

function pruneAndRate(window, now) {
  const kept = window.filter((entry) => entry.t >= now - METRICS_RATE_WINDOW_MS)
  const bytes = kept.reduce((sum, entry) => sum + entry.bytes, 0)
  return {
    kept,
    rate: Math.round(bytes / (METRICS_RATE_WINDOW_MS / 1000)),
  }
}

function updateMessageRates(state, direction, bytes) {
  const now = nowMs()
  const key = direction === 'in' ? 'inWindow' : 'outWindow'
  const rateKey = direction === 'in' ? 'inRateBytesPerSec' : 'outRateBytesPerSec'
  state.metrics.messages[key].push({ t: now, bytes })
  const { kept, rate } = pruneAndRate(state.metrics.messages[key], now)
  state.metrics.messages[key] = kept
  state.metrics.messages[rateKey] = rate
}

function updateRttAggregates(state) {
  const values = state.metrics.latency.samples
  if (!values.length) {
    state.metrics.latency.avgRttMs = null
    state.metrics.latency.p95RttMs = null
    return
  }

  const avg = values.reduce((sum, value) => sum + value, 0) / values.length
  const sorted = [...values].sort((a, b) => a - b)
  const p95Index = Math.min(sorted.length - 1, Math.ceil(sorted.length * 0.95) - 1)
  state.metrics.latency.avgRttMs = Math.round(avg)
  state.metrics.latency.p95RttMs = sorted[p95Index]
}

export default {
  namespaced: true,
  state: {
    network: null,
    connection: null,
    connectionStatus: 'disconnected',
    networkMetricsVisible: false,
    session,
    room: createDefaultRoom(),
    metrics: createDefaultMetrics(),
  },
  mutations: {
    updatePlayers(state, payload) {
      state.room.players = payload.players
    },
    setNetworkMetricsVisible(state, isVisible) {
      state.networkMetricsVisible = Boolean(isVisible)
    }
  },
  actions: {
    setNetworkMetricsVisible({ commit }, isVisible) {
      commit('setNetworkMetricsVisible', isVisible)
    },
    setup({ state, dispatch }, payload) {
      console.log('setup network', Network, state)
      const network = Network.setup(payload.bugout, state.session, {
        onSend: (message) => {
          dispatch('recordOutgoingMessage', message)
        },
        onReceive: (message) => {
          dispatch('recordIncomingMessage', message)
        },
      })

      payload.bugout.on('seen', () => {
        state.metrics.bugs.seenAt = nowMs()
        if (state.connectionStatus !== 'connected') {
          state.connectionStatus = 'connected'
          dispatch('markConnected')
        }
      })

      payload.bugout.on('left', () => {
        state.metrics.bugs.leftAt = nowMs()
        if (state.session.isController()) {
          state.connectionStatus = 'disconnected'
          state.metrics.lastDisconnectAt = nowMs()
          dispatch('stopMetricsPing')
        }
      })

      payload.bugout.on('timeout', () => {
        state.metrics.bugs.timeoutAt = nowMs()
      })

      payload.bugout.on('announce', () => {
        state.metrics.bugs.announceAt = nowMs()
      })

      payload.bugout.on('tracker', (_, update) => {
        state.metrics.bugs.trackerAt = nowMs()
        state.metrics.bugs.trackerPeers = get(update, 'complete', null)
      })

      payload.bugout.on('connections', (count) => {
        state.metrics.bugs.wires = count
      })

      payload.bugout.on('wireseen', () => {
        state.metrics.bugs.wireSeenAt = nowMs()
      })

      payload.bugout.on('wireleft', () => {
        state.metrics.bugs.wireLeftAt = nowMs()
      })

      payload.bugout.on('server', () => {
        state.metrics.bugs.serverAt = nowMs()
      })

      network.callbacks['request-room-info'] = (message) => {
        console.log('request room info', message.msg)
        if (state.session.isScreen()) {
          dispatch('sendRoomInfo')
        }
      }

      network.callbacks['send-room-info'] = (message) => {
        console.log('send room info', message.msg)
        if (state.session.isController()) {
          console.log('received room info', message.msg)
          state.room = message.msg
          ensureRoomShape(state)
          buildCasualSelectedPlayersFromSlots(state)
        }

        if (state.session.isScreen() && message.msg.playing) {
          state.room.playing = true
        }
      }

      network.callbacks['request-set-player'] = (message) => {
        if (state.session.isScreen()) {
          dispatch('setPlayer', message)
        }
      }

      network.callbacks['request-set-slot'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('setSlot', message.msg)
        }
      }

      network.callbacks['request-set-host'] = (message) => {
        if (state.session.isScreen()) {
          dispatch('setHost', {
            identity: get(message, 'cid'),
          })
        }
      }

      network.callbacks['request-add-debug-bot'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('addDebugBot', message.msg)
        }
      }

      network.callbacks['request-remove-debug-bot'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('removeDebugBot', message.msg)
        }
      }

      network.callbacks['request-set-debug-bot-profile'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('setDebugBotProfile', message.msg)
        }
      }

      network.callbacks['request-clear-slot'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('clearSlot', message.msg)
        }
      }

      network.callbacks['request-start-game'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('startGame')
        }
      }

      network.callbacks['request-stop-game'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('stopGame')
        }
      }

      network.callbacks['request-create-tournament'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('createTournament', message.msg)
        }
      }

      network.callbacks['request-start-tournament'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('startTournament')
        }
      }

      network.callbacks['request-start-next-match'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('startNextMatch')
        }
      }

      network.callbacks['request-report-match-finished'] = (message) => {
        if (state.session.isScreen()) {
          dispatch('reportMatchFinished', message.msg)
        }
      }

      network.callbacks['request-reset-tournament'] = (message) => {
        if (state.session.isScreen() && isHostRequester(state, message)) {
          dispatch('resetTournament')
        }
      }

      network.callbacks['disconnected-player'] = (message) => {
        console.log('disconnected player', message.msg)
      }

      network.callbacks['player-ready'] = (message) => {
        console.log('player is ready ', message, state)
        dispatch('addPlayer', message.msg)
      }

      network.callbacks['profile-change'] = (message) => {
        console.log('profile changed', message)
        dispatch('updateProfile', message.msg)
      }

      network.callbacks['metrics-ping'] = (message) => {
        if (!state.session.isScreen()) {
          return
        }
        state.network.send('metrics-pong', {
          id: get(message, 'msg.id'),
        })
      }

      network.callbacks['metrics-pong'] = (message) => {
        if (!state.session.isController()) {
          return
        }
        const pingId = get(message, 'msg.id')
        if (!pingId) {
          return
        }
        const sentAt = state.metrics.latency.pendingPings[pingId]
        if (!sentAt) {
          return
        }
        const rtt = Math.max(0, nowMs() - sentAt)
        delete state.metrics.latency.pendingPings[pingId]
        state.metrics.latency.lastRttMs = rtt
        state.metrics.latency.lastPongAt = nowMs()
        state.metrics.latency.samples.push(rtt)
        if (state.metrics.latency.samples.length > METRICS_MAX_RTT_SAMPLES) {
          state.metrics.latency.samples.shift()
        }
        updateRttAggregates(state)
      }

      state.network = network
    },

    markConnected({ state, dispatch }) {
      const connectedAt = nowMs()
      state.metrics.connectedAt = connectedAt
      if (state.metrics.connectAttemptStartedAt) {
        state.metrics.connectDurationMs = connectedAt - state.metrics.connectAttemptStartedAt
      }

      if (state.session.isController()) {
        dispatch('startMetricsPing')
      }
    },

    recordOutgoingMessage({ state }, payload) {
      const bytes = estimateBytes(payload)
      state.metrics.messages.outCount += 1
      state.metrics.messages.outBytes += bytes
      updateMessageRates(state, 'out', bytes)
    },

    recordIncomingMessage({ state }, payload) {
      const bytes = estimateBytes(payload)
      state.metrics.messages.inCount += 1
      state.metrics.messages.inBytes += bytes
      updateMessageRates(state, 'in', bytes)
    },

    startMetricsPing({ state, dispatch }) {
      dispatch('stopMetricsPing')
      if (!state.network || !state.session.isController()) {
        return
      }

      state.metrics.pingTimerId = setInterval(() => {
        if (!state.network || state.connectionStatus !== 'connected') {
          return
        }
        const pingId = `${nowMs()}-${Math.random().toString(36).slice(2, 8)}`
        state.metrics.latency.pendingPings[pingId] = nowMs()
        state.metrics.latency.lastPingSentAt = nowMs()
        state.network.send('metrics-ping', { id: pingId })
      }, state.metrics.pingIntervalMs)
    },

    stopMetricsPing({ state }) {
      if (state.metrics.pingTimerId) {
        clearInterval(state.metrics.pingTimerId)
        state.metrics.pingTimerId = null
      }
      state.metrics.latency.pendingPings = {}
    },

    requestRoomInfo({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-room-info', {})
    },

    sendRoomInfo({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('send-room-info', state.room)
    },

    disconnect({ state, dispatch }) {
      dispatch('stopMetricsPing')
      if (state.network && state.network.bugout) {
        const connection = state.network.bugout
        if (typeof connection.destroy === 'function') {
          connection.destroy()
        } else if (typeof connection.close === 'function') {
          connection.close()
        }
      }

      state.network = null
      state.connection = null
      state.connectionStatus = 'disconnected'
      state.room = createDefaultRoom()
      state.metrics.lastDisconnectAt = nowMs()
      state.metrics.bugs.wires = 0
    },

    resetSession({ state, dispatch }) {
      dispatch('disconnect')
      state.session.clear()
    },

    async createServer({ state, dispatch }) {
      await dispatch('disconnect')
      state.connectionStatus = 'connecting'
      state.metrics.connectAttemptStartedAt = nowMs()
      state.metrics.role = 'screen'

      const b = new Bugout({
        seed: localStorage['bugout-server-seed'],
        announce: networkConfig.announce,
        iceServers: networkConfig.iceServers,
      })

      localStorage['bugout-server-seed'] = b.seed
      await dispatch('setup', { bugout: b })
      state.connection = {
        role: 'screen',
        peerId: b.address(),
      }
      state.connectionStatus = 'connected'
      dispatch('markConnected')
      state.session.setModeScreen()
    },

    async joinRoom({ state, dispatch }, payload) {
      console.log('joinRoom')
      await dispatch('disconnect')
      state.connectionStatus = 'connecting'
      state.metrics.connectAttemptStartedAt = nowMs()
      state.metrics.role = 'controller'

      const b = new Bugout(payload.roomName, {
        announce: networkConfig.announce,
        iceServers: networkConfig.iceServers,
      })

      await dispatch('setup', {
        bugout: b
      })

      b.on('server', (address) => {
        console.log('connected to server', address)
        state.session.setRoomName(payload.roomName)
        state.session.setModeController()
        state.connection = {
          role: 'controller',
          peerId: b.address(),
        }
        state.connectionStatus = 'connected'
        dispatch('markConnected')
        state.network.send('player-ready', {
          peerId: state.network.bugout.address(),
          identity: state.session.identity,
          chosenName: state.session.chosenName
        })
      })

      setTimeout(() => {
        if (state.connectionStatus === 'connecting') {
          state.connectionStatus = 'disconnected'
          state.metrics.lastDisconnectAt = nowMs()
        }
      }, 8000)

      localStorage['bugout-seed'] = JSON.stringify(b.seed)
    },

    addPlayer({ state, dispatch }, payload) {
      console.log('addPlayer', state, payload)
      let player = state.room.players.find((p) => p.identity === payload.identity)
      if (!player) {
        state.room.players.push({
          identity: payload.identity,
          peerId: payload.peerId,
          chosenName: payload.chosenName,
          connected: true
        })
      } else {
        player.peerId = payload.peerId
        player.chosenName = payload.chosenName
        player.connected = true
      }

      if (!state.room.gameHost) {
        state.room.gameHost = payload.identity
      }

      dispatch('sendRoomInfo')
    },

    removePlayer({ state, dispatch }, payload) {
      const player = state.room.players.find((p) => p.peerId === payload.player.peerId)
      if (player) {
        player.connected = false
        if (state.room.gameHost === player.identity) {
          const replacementHost = state.room.players.find((p) => p.connected !== false && p.identity !== player.identity && p.isBot !== true)
            || state.room.players.find((p) => p.connected !== false && p.identity !== player.identity)
          state.room.gameHost = replacementHost ? replacementHost.identity : null
        }
      }
      dispatch('sendRoomInfo')
    },

    addDebugBot({ state, dispatch }) {
      ensureRoomShape(state)
      const bots = getDebugBots(state)
      const nextNumber = bots.reduce((maxNumber, bot) => {
        const parsed = parseDebugBotNumber(bot.identity)
        return parsed && parsed > maxNumber ? parsed : maxNumber
      }, 0) + 1
      state.room.players.push(createDebugBot(nextNumber))
      dispatch('sendRoomInfo')
    },

    setDebugBotProfile({ state, dispatch }, payload) {
      ensureRoomShape(state)
      const profile = get(payload, 'profile')
      if (!DEBUG_BOT_PROFILES.includes(profile)) {
        return
      }
      const identity = get(payload, 'identity')
      const bot = identity
        ? state.room.players.find((player) => player.identity === identity && isDebugBotIdentity(player.identity))
        : [...getDebugBots(state)].sort(sortDebugBots)[0]
      if (!bot) {
        return
      }
      bot.botProfile = profile
      dispatch('sendRoomInfo')
    },

    removeDebugBot({ state, dispatch }, payload = {}) {
      ensureRoomShape(state)
      const targetIdentity = get(payload, 'identity')
      let botIdentity = null

      if (targetIdentity && isDebugBotIdentity(targetIdentity)) {
        botIdentity = targetIdentity
      } else {
        const bots = [...getDebugBots(state)].sort(sortDebugBots)
        const lastBot = bots[bots.length - 1]
        botIdentity = lastBot ? lastBot.identity : null
      }

      if (!botIdentity) {
        return
      }

      const botIndex = state.room.players.findIndex((player) => player.identity === botIdentity)
      if (botIndex < 0) {
        return
      }

      state.room.players.splice(botIndex, 1)

      if (state.room.lobby.slots[1] === botIdentity) {
        state.room.lobby.slots[1] = null
      }
      if (state.room.lobby.slots[2] === botIdentity) {
        state.room.lobby.slots[2] = null
      }

      buildCasualSelectedPlayersFromSlots(state)
      dispatch('sendRoomInfo')
    },

    updateProfile({ state, commit }, payload) {
      console.log('process update', payload)
      const player = state.room.players.find((p) => p.identity === payload.identity)
      if (player) {
        player.chosenName = payload.chosenName
        player.peerId = payload.peerId
      }
      console.log('updated chosenName', state, this.state)
      commit('updatePlayers', {
        players: [...state.room.players]
      })
    },

    requestSetPlayer({ dispatch }, payload) {
      const identity = get(payload, 'player.identity')
      const id = get(payload, 'id')
      if (!identity || !isValidSlot(id)) {
        return
      }
      dispatch('requestSetSlot', { id, identity })
    },

    requestSetSlot({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-set-slot', payload)
    },

    requestSetHost({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-set-host', payload)
    },

    requestAddDebugBot({ state }, payload = {}) {
      if (!state.network) {
        return
      }
      state.network.send('request-add-debug-bot', payload)
    },

    requestRemoveDebugBot({ state }, payload = {}) {
      if (!state.network) {
        return
      }
      state.network.send('request-remove-debug-bot', payload)
    },

    requestSetDebugBotProfile({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-set-debug-bot-profile', payload)
    },

    requestClearSlot({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-clear-slot', payload)
    },

    setPlayer({ dispatch }, payload) {
      const identity = get(payload, 'msg.player.identity')
      const id = get(payload, 'msg.id')
      if (!identity || !isValidSlot(id)) {
        return
      }
      dispatch('setSlot', { id, identity })
    },

    setSlot({ state, dispatch }, payload) {
      ensureRoomShape(state)
      const slotId = normalizeSlotId(payload.id)
      if (!isValidSlot(slotId)) {
        return
      }

      const identity = payload.identity
      if (!identity) {
        return
      }

      if (state.room.lobby.slots[1] === identity) {
        state.room.lobby.slots[1] = null
      }
      if (state.room.lobby.slots[2] === identity) {
        state.room.lobby.slots[2] = null
      }

      state.room.lobby.slots[slotId] = identity
      buildCasualSelectedPlayersFromSlots(state)
      dispatch('sendRoomInfo')
    },

    setHost({ state, dispatch }, payload) {
      ensureRoomShape(state)
      const identity = get(payload, 'identity')
      if (!identity) {
        return
      }
      const player = state.room.players.find((p) => p.identity === identity)
      if (!player || player.connected === false) {
        return
      }
      state.room.gameHost = identity
      dispatch('sendRoomInfo')
    },

    clearSlot({ state, dispatch }, payload) {
      ensureRoomShape(state)
      const slotId = normalizeSlotId(payload.id)
      if (!isValidSlot(slotId)) {
        return
      }
      state.room.lobby.slots[slotId] = null
      buildCasualSelectedPlayersFromSlots(state)
      dispatch('sendRoomInfo')
    },

    requestStartGame({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-start-game')
    },

    requestStopGame({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-stop-game')
    },

    startGame({ state, dispatch }) {
      console.log('startGame')
      state.room.playing = true
      dispatch('sendRoomInfo')
    },

    stopGame({ state, dispatch }) {
      console.log('stopGame')
      state.room.playing = false
      dispatch('sendRoomInfo')
    },

    requestCreateTournament({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-create-tournament', payload)
    },

    requestStartTournament({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-start-tournament', {})
    },

    requestStartNextMatch({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-start-next-match', {})
    },

    requestReportMatchFinished({ state }, payload) {
      if (!state.network) {
        return
      }
      state.network.send('request-report-match-finished', payload)
    },

    requestResetTournament({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('request-reset-tournament', {})
    },

    createTournament({ state, dispatch }, payload) {
      ensureRoomShape(state)

      const mode = payload.mode
      if (mode !== 'round_robin' && mode !== 'knockout') {
        return
      }

      const participants = (payload.participants || []).filter(Boolean)
      if (participants.length < 2) {
        return
      }

      const config = {
        ...state.room.tournament.config,
        ...(payload.config || {}),
      }

      let fixtures = []
      let bracket = null
      let standings = {}

      if (mode === 'round_robin') {
        fixtures = buildRoundRobinFixtures(participants, config.roundRobinLegs || 1)
        standings = createStandings(participants)
      } else {
        const built = buildKnockoutBracket(participants)
        fixtures = built.fixtures
        bracket = built.bracket
        settleByes({ fixtures })
      }

      state.room.tournament = {
        mode,
        status: 'ready',
        participants,
        fixtures,
        currentFixtureId: null,
        results: [],
        standings,
        bracket,
        config,
        championIdentity: null,
      }

      dispatch('sendRoomInfo')
    },

    startTournament({ state, dispatch }) {
      ensureRoomShape(state)
      if (!state.room.tournament || state.room.tournament.mode === 'casual') {
        return
      }

      const nextFixture = getNextPendingFixture(state.room.tournament)
      if (!nextFixture) {
        state.room.tournament.status = 'completed'
        dispatch('sendRoomInfo')
        return
      }

      state.room.tournament.currentFixtureId = nextFixture.id
      state.room.tournament.status = 'in_match'

      state.room.lobby.slots[1] = nextFixture.player1Identity
      state.room.lobby.slots[2] = nextFixture.player2Identity
      buildCasualSelectedPlayersFromSlots(state)

      state.room.playing = true
      dispatch('sendRoomInfo')
    },

    startNextMatch({ state, dispatch }) {
      dispatch('startTournament')
    },

    reportMatchFinished({ state, dispatch }, payload) {
      ensureRoomShape(state)
      const tournament = state.room.tournament
      if (!tournament || tournament.mode === 'casual') {
        return
      }

      const result = {
        fixtureId: payload.fixtureId || tournament.currentFixtureId,
        player1Identity: payload.player1Identity,
        player2Identity: payload.player2Identity,
        score1: payload.score1,
        score2: payload.score2,
        winnerIdentity: payload.winnerIdentity,
        finishedAt: payload.finishedAt || Date.now(),
      }

      if (!result.fixtureId || !result.winnerIdentity) {
        return
      }

      if (tournament.mode === 'round_robin') {
        upsertFixtureResult(tournament.fixtures, result)
        tournament.standings = applyMatchResult(tournament.standings, result)
      } else {
        applyKnockoutAdvance(tournament, result)
        const finalFixture = [...tournament.fixtures]
          .filter((f) => f.round === tournament.bracket.rounds)
          .find((f) => f.status === 'done' && f.winnerIdentity)
        if (finalFixture) {
          tournament.championIdentity = finalFixture.winnerIdentity
        }
      }

      tournament.results.push(result)
      state.room.playing = false

      const nextFixture = getNextPendingFixture(tournament)
      if (nextFixture) {
        tournament.currentFixtureId = nextFixture.id
        tournament.status = 'ready'
      } else {
        tournament.currentFixtureId = null
        tournament.status = 'completed'
      }

      dispatch('sendRoomInfo')
    },

    resetTournament({ state, dispatch }) {
      state.room.tournament = createDefaultRoom().tournament
      state.room.lobby.slots[1] = null
      state.room.lobby.slots[2] = null
      buildCasualSelectedPlayersFromSlots(state)
      dispatch('sendRoomInfo')
    },

    broadCastProfile({ state }) {
      if (!state.network) {
        return
      }
      state.network.send('profile-change', {
        peerId: state.network.bugout.address(),
        identity: state.session.identity,
        chosenName: state.session.chosenName
      })
    },

    setName({ state, dispatch }, payload) {
      state.session.setChosenName(payload.name)
      dispatch('broadCastProfile')
    },

    reconnect({ state, dispatch }) {
      state.metrics.reconnectCount += 1
      if (state.session.isController() && state.session.roomName) {
        return dispatch('joinRoom', {
          roomName: state.session.roomName,
        })
      }

      if (state.session.isScreen()) {
        return dispatch('createServer')
      }

      return dispatch('disconnect')
    }
  },

  getters: {
    network: (state) => state.network,
    room: (state) => state.room,
    gameHost: (state) => get(state, 'room.gameHost', null),
    connection: (state) => state.connection,
    connectionStatus: (state) => state.connectionStatus,
    networkMetricsVisible: (state) => state.networkMetricsVisible,
    session: (state) => state.session,
    playing: (state) => state.room.playing,
    address: (state) => get(state, 'network.bugout.identifier'),
    chosenName: (state) => get(state, 'session.chosenName'),
    players: (state) => {
      try {
        return state.room.players
      } catch (e) {
        return []
      }
    },
    selectedPlayers: (state) => state.room.selectedPlayers,
    slots: (state) => get(state, 'room.lobby.slots', { 1: null, 2: null }),
    slotPlayer1: (state) => get(state, 'room.lobby.slots.1', null),
    slotPlayer2: (state) => get(state, 'room.lobby.slots.2', null),
    tournament: (state) => get(state, 'room.tournament'),
    currentFixture: (state) => {
      const tournament = get(state, 'room.tournament')
      if (!tournament || !tournament.currentFixtureId) {
        return null
      }
      return tournament.fixtures.find((f) => f.id === tournament.currentFixtureId) || null
    },
    tournamentStandings: (state) => get(state, 'room.tournament.standings', {}),
    metrics: (state) => state.metrics,
  },
}
